import { establishPublishingConnectionSaga } from 'features/streaming/sagas/establishPublishingConnectionSaga';
import { call, fork, put, select } from 'redux-saga/effects';
import { Action } from '@reduxjs/toolkit';
import { User, UserId } from 'features/users/types';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes, RoleEntries } from 'features/permissions/types';
import {
  managePrivateWhiteboardPermissionRevoked,
  signalingRoleChanged,
  whiteboardPermissionGranted,
  whiteboardPermissionRevoked,
} from 'features/permissions/actions';
import { changeRole, selectLocalUserId, selectUserById } from 'features/users/usersSlice';
import { eventBus } from 'utils/eventBus';
import { logger } from 'utils/logger';
import {
  selectCanManagePrivateWhiteboards,
  selectRoleEntries,
} from 'features/permissions/permissionsSlice';
import { notification } from 'features/notifications/toast/notification';
import i18n from 'i18n';
import { lobbyJoinSoundManager } from 'utils/audio/LobbyJoinSoundManager';
import { handleRoleChangeBroadcastPermissionsSaga } from 'features/permissions/sagas/handleRoleChangeBroadcastPermissionsSaga';
import { handleRoleChangeQAPermissionsSaga } from 'features/permissions/sagas/handleRoleChangeQAPermissionsSaga';
import { RTCClient } from 'utils/webrtc';
import { clearWaitingUsersList } from 'features/users/actions';
import { WAITING_ENTRY_NOTIFICATION_ID } from 'features/notifications/constants';
import { closeActionNotification } from 'features/notifications/thunks/closeActionNotification';
import { raiseHandNotificationUpdateRequested } from 'features/notifications/actions';
import { cleanBreakoutRooms } from 'features/breakout-rooms/breakoutRoomsSlice';

import { syncChatCountersWithPermissionsSaga } from 'features/chat/sagas/syncChatCountersWithPermissionsSaga';
import { collaborationConfigReset } from 'features/shared-notes/sharedNotesSlice';
import { cleanupBreakoutRoomsNotificationsSaga } from 'features/breakout-rooms/sagas/cleanupBreakoutRoomsNotificationsSaga';
import { handleRoleChangeQuizPermissionsSaga } from 'features/permissions/sagas/handleRoleChangeQuizPermissionsSaga';
import { handleRoleChangePollsPermissionsSaga } from 'features/permissions/sagas/handleRoleChangePollsPermissionsSaga';
import { reconcilePresentContentLibraryPermissionsSaga } from 'features/permissions/sagas/reconcilePresentContentLibraryPermissionsSaga';
import { setSentryUserDetails } from 'utils/sentry/setSentryUserDetails';
import { updateToolbarUserCountSaga } from 'features/toolbar/sagas/updateToolbarUserCountSaga';
import { selectIsContentLocked } from 'features/layout/features/content/contentSlice';
import { navigateToLockedContent } from 'features/layout/features/content/actions';
import { reEvaluateAccessiblePinnedPanelsSaga } from 'features/side-panels/sagas/reEvaluateAccessiblePinnedPanelsSaga';

export function* onRoleChangedSaga({ payload }: ReturnType<typeof signalingRoleChanged>) {
  const { role, id, initiator } = payload;

  const user: User = yield select(selectUserById, id);
  if (!user) {
    return;
  }

  yield put(changeRole(payload));

  const localUserId: UserId = yield select(selectLocalUserId);

  const affectsLocalUser = localUserId === id;

  if (affectsLocalUser) {
    const initiatorUser: User | undefined = initiator
      ? yield select(selectUserById, initiator.id)
      : undefined;

    logger
      .remote({ tier: 1 })
      .info(
        initiator
          ? `The user's role was changed to ${role} by user id=${initiator.id}`
          : `The user's role was changed to ${role} with no initiator`
      );

    const roles: RoleEntries = yield select(selectRoleEntries);
    const roleName = roles[role]?.displayName ?? role;

    yield call(
      notification,
      initiatorUser?.name
        ? i18n.t('notifications:role_changed', { username: initiatorUser.name, role: roleName })
        : i18n.t('notifications:role_changed_unnamed', { role: roleName })
    );
  }

  if (affectsLocalUser) {
    const userCanControlRoomEntry: boolean = yield call(
      hasPermissionsSaga,
      PermissionTypes.controlRoomEntry
    );

    // clear waiting users
    if (!userCanControlRoomEntry) {
      yield put(clearWaitingUsersList());

      // hide waiting entry action notification
      const cleanupNotificationThunk: Action = yield call(
        closeActionNotification,
        WAITING_ENTRY_NOTIFICATION_ID
      );
      yield put(cleanupNotificationThunk);

      // reset lobby join sound manager
      yield call(lobbyJoinSoundManager.reset);
    }

    const userCanManageBreakoutRooms: boolean = yield call(
      hasPermissionsSaga,
      PermissionTypes.manageBreakout
    );

    // clear breakout rooms
    if (!userCanManageBreakoutRooms) {
      yield put(cleanBreakoutRooms());

      yield fork(cleanupBreakoutRoomsNotificationsSaga);
    }

    yield fork(handleRoleChangeQAPermissionsSaga);

    yield fork(handleRoleChangeQuizPermissionsSaga);

    yield fork(syncChatCountersWithPermissionsSaga);

    yield fork(handleRoleChangePollsPermissionsSaga);

    yield fork(reconcilePresentContentLibraryPermissionsSaga);

    yield call(setSentryUserDetails, { role });

    yield fork(updateToolbarUserCountSaga);

    const canLockContentWithNewRole: boolean = yield call(
      hasPermissionsSaga,
      PermissionTypes.lockContent,
      { role }
    );

    const isContentLocked: boolean = yield select(selectIsContentLocked);

    if (!canLockContentWithNewRole && isContentLocked) {
      yield put(navigateToLockedContent());
    }

    yield call(reEvaluateAccessiblePinnedPanelsSaga);
  }

  yield put(raiseHandNotificationUpdateRequested({ reason: 'role-changed' }));

  const { role: oldRole } = user;

  const oldRoleScreenshareAllowed: boolean = yield call(
    hasPermissionsSaga,
    PermissionTypes.screenshare,
    {
      role: oldRole,
    }
  );

  const oldRoleWBAllowed: boolean = yield call(hasPermissionsSaga, PermissionTypes.editWhiteboard, {
    role: oldRole,
  });

  const oldRoleManagePrivateWBAllowed: boolean = yield select(selectCanManagePrivateWhiteboards, {
    role: oldRole,
  });

  const newRoleScreenshareAllowed: boolean = yield call(
    hasPermissionsSaga,
    PermissionTypes.screenshare,
    {
      role,
    }
  );

  const newRoleWBAllowed: boolean = yield call(hasPermissionsSaga, PermissionTypes.editWhiteboard, {
    role,
  });

  const newRoleManagePrivateWBAllowed: boolean = yield select(selectCanManagePrivateWhiteboards, {
    role,
  });

  const screensharePermissionsGranted = !oldRoleScreenshareAllowed && newRoleScreenshareAllowed;
  const screensharePermissionsRevoked = oldRoleScreenshareAllowed && !newRoleScreenshareAllowed;

  const whiteboardPermissionsGranted = !oldRoleWBAllowed && newRoleWBAllowed;
  const whiteboardPermissionsRevoked = oldRoleWBAllowed && !newRoleWBAllowed;

  const managePrivateWhiteboardPermissionsRevoked =
    oldRoleManagePrivateWBAllowed && !newRoleManagePrivateWBAllowed;

  const oldRoleHadNotesAccess: boolean = yield call(
    hasPermissionsSaga,
    [PermissionTypes.collaborateInNotes, PermissionTypes.seeNotes],
    {
      mode: 'any',
      role: oldRole,
    }
  );

  const newRoleHasNoNotesAccess: boolean = yield call(
    hasPermissionsSaga,
    [PermissionTypes.collaborateInNotes, PermissionTypes.seeNotes],
    {
      mode: 'all',
      role,
    }
  );

  const notesPermissionsRevoked = oldRoleHadNotesAccess && !newRoleHasNoNotesAccess;

  if (affectsLocalUser && notesPermissionsRevoked) {
    yield put(collaborationConfigReset());
  }

  if (affectsLocalUser && screensharePermissionsGranted) {
    yield call(establishPublishingConnectionSaga, false);
    yield call(eventBus.sendMessage, 'permissionsChanged', {}, { screenshare: true });
  }

  if (affectsLocalUser && screensharePermissionsRevoked) {
    yield call(RTCClient.screensharingFeed.stopScreenshare, false);
    yield call(eventBus.sendMessage, 'permissionsChanged', {}, { screenshare: false });
  }

  yield call(eventBus.sendMessage, 'roleChanged', {}, { userId: id, from: oldRole, to: role });

  if (whiteboardPermissionsGranted) {
    yield put(whiteboardPermissionGranted(payload));
  }

  if (whiteboardPermissionsRevoked) {
    yield put(whiteboardPermissionRevoked(payload));
  }

  if (managePrivateWhiteboardPermissionsRevoked) {
    yield put(managePrivateWhiteboardPermissionRevoked(payload));
  }

  yield call(handleRoleChangeBroadcastPermissionsSaga, payload, user);
}
